import { yupResolver } from "@hookform/resolvers/yup";
import {
  useGetEmpKetInformationQuery,
  useInsertKetInformationMutation,
} from "AppQuries/employee/ket-information";
import Input from "Components/UI/Input";
import { useFormattedEmploymentTypeOption } from "core/Operations/formattedOption/useFormattedEmployeeOptions";
import { toast } from "core/Operations/toast";
import ketInformationValidationSchema from "core/Operations/ValidationSchema/ketInformationValidation";
import { loggedInUser } from "core/redux/slice/authSlice";
import LoadingSpinner from "InitialPage/Sidebar/LoadingSpinner";
import Error404 from "MainPages/ErrorPages/error404";
import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Save } from "react-feather";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "Router/all_routes";

const KetForm = () => {
  const userData = useSelector(loggedInUser);
  const { employeeCode } = useParams();
  const navigate = useNavigate();

  const { mutate: updateKetInformation } = useInsertKetInformationMutation();

  const { data, isLoading, isError, error } = useGetEmpKetInformationQuery(
    {
      companyCode: userData?.companyCode,
      employeeCode: employeeCode,
    },
    {
      enabled: !!userData?.companyCode && !!employeeCode,
    }
  );

  const employementTypeOptions = useFormattedEmploymentTypeOption(
    userData?.companyCode
  );

  const methods = useForm({
    defaultValues: {
      companyCode: userData?.companyCode ?? "",
      employeeCode: employeeCode ?? "",
    },
    resolver: yupResolver(ketInformationValidationSchema),
  });

  React.useEffect(() => {
    if (data) {
      methods.reset({
        companyCode: userData?.companyCode,
        ...data,
        employeeCode: employeeCode,
      });
    }
  }, [data, userData?.companyCode, employeeCode]);

  const {
    setValue,
    watch,
    handleSubmit,
    trigger,
    getValues,
    control,
    formState: { errors, isSubmitted, isSubmitting },
  } = methods;

  const onSubmit = (payload) => {
    updateKetInformation(payload, {
      onSuccess: (data) => {
        if (data[0].response === true) {
          toast.success(
            data[0].errorMessage || "KET Information Updated Successfully."
          );
          setTimeout(() => {
            navigate(`${all_routes.ket_information}`);
          });
        } else {
          toast.warning(data[0].errorMessage || "Something went wrong.");
        }
      },
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <Error404 error={error} />;
  return (
    <div className="page-wrapper">
      <div className="content">
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="needs-validation"
            noValidate=""
          >
            <div className="page-header">
              <div className="add-item d-flex">
                <div className="page-title">
                  <h4>KET Information</h4>
                  <h6>Update Employee KET Information</h6>
                </div>
              </div>
              <div className="page-btn">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="add-new-tooltip">Back</Tooltip>}
                >
                  <Link
                    to={`${all_routes.ket_information}`}
                    className="btn btn-secondary"
                  >
                    Back
                  </Link>
                </OverlayTrigger>
              </div>
              <div className="page-btn">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="Save">Save</Tooltip>}
                >
                  <button
                    type="submit"
                    className="btn btn-added"
                    disabled={isSubmitting || isSubmitted}
                  >
                    {isSubmitting || isSubmitted ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Save className="me-2" />
                        Save
                      </>
                    )}
                  </button>
                </OverlayTrigger>
              </div>
            </div>

            <div className="card border-0">
              <div className="card-header">
                <h4 className="card-title">KET Form</h4>
              </div>
              <div className="card-body">
                <div className="row form-row">
                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="employeeFullName"
                      type="text"
                      placeholder="Employee Full Name"
                      className={`form-control ${
                        errors.employeeFullName ? "is-invalid" : ""
                      }`}
                      label="Employee Full Name"
                      labelClassName="form-label"
                      required={true}
                      requiredMessage=""
                      defaultValue=""
                      readOnly={true}
                    />
                  </div>
                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="companyName"
                      type="text"
                      placeholder="Company Name"
                      className={`form-control ${
                        errors.companyName ? "is-invalid" : ""
                      }`}
                      label="Company Name"
                      labelClassName="form-label"
                      required={true}
                      requiredMessage="Company Name is required"
                      defaultValue=""
                      readOnly={true}
                    />
                  </div>
                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="placeOfWork"
                      type="text"
                      placeholder="Place Of Work"
                      className={`form-control ${
                        errors.placeOfWork ? "is-invalid" : ""
                      }`}
                      label="Place Of Work"
                      labelClassName="form-label"
                      required={true}
                      requiredMessage="Place Of Work is required"
                      defaultValue=""
                      readOnly={true}
                    />
                  </div>
                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="employeeNRIC"
                      type="text"
                      placeholder="Employee NRIC"
                      className={`form-control ${
                        errors.employeeNRIC ? "is-invalid" : ""
                      }`}
                      label="Employee NRIC"
                      labelClassName="form-label"
                      required={true}
                      requiredMessage=""
                      defaultValue=""
                      readOnly={true}
                    />
                  </div>
                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="jobTitle"
                      type="text"
                      placeholder="Job Title"
                      className={`form-control ${
                        errors.jobTitle ? "is-invalid" : ""
                      }`}
                      label="Job Title"
                      labelClassName="form-label"
                      required={false}
                      requiredMessage=""
                      defaultValue=""
                      readOnly={true}
                    />
                  </div>
                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="mainDuties"
                      type="text"
                      placeholder="Main Duties"
                      className={`form-control ${
                        errors.mainDuties ? "is-invalid" : ""
                      }`}
                      label="Main Duties"
                      labelClassName="form-label"
                      required={false}
                      requiredMessage=""
                      defaultValue=""
                      // readOnly={true}
                      maxLength={200}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3 ">
                    <Input
                      name="employmentType"
                      type="commonSelect"
                      placeholder=""
                      className={`form-control ${
                        errors.employmentType ? "is-invalid" : ""
                      }`}
                      label="Employment Type"
                      labelClassName="form-label"
                      optionData={employementTypeOptions}
                      required={true}
                      requiredMessage=""
                      onSelect={(selectedOption) => {
                        setValue("employmentType", selectedOption);
                      }}
                      defaultValue={watch("employmentType")}
                      disabled={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="startDate"
                      type="date"
                      placeholder="Start Date"
                      className={`form-control ${
                        errors.startDate ? "is-invalid" : ""
                      }`}
                      label="Start Date"
                      labelClassName="form-label"
                      defaultValue=""
                      dateFormat="YYYY-MM-DD"
                      required={true}
                      disabled={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="endDate"
                      type="date"
                      placeholder="End Date"
                      className={`form-control ${
                        errors.endDate ? "is-invalid" : ""
                      }`}
                      label="End Date"
                      labelClassName="form-label"
                      defaultValue=""
                      dateFormat="YYYY-MM-DD"
                      required={false}
                      disabled={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="workingHours"
                      type="text"
                      placeholder="Working Hours"
                      className={`form-control ${
                        errors.workingHours ? "is-invalid" : ""
                      }`}
                      label="Working Hours"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      // readOnly={false}
                      maxLength={250}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="workingDays"
                      type="text"
                      placeholder="Working Days"
                      className={`form-control ${
                        errors.workingDays ? "is-invalid" : ""
                      }`}
                      label="Working Days"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      // readOnly={false}
                      maxLength={100}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="breakTime"
                      type="text"
                      placeholder="Break Time"
                      className={`form-control ${
                        errors.breakTime ? "is-invalid" : ""
                      }`}
                      label="Break Time"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      // readOnly={false}
                      maxLength={100}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="restDay"
                      type="text"
                      placeholder="Rest Day"
                      className={`form-control ${
                        errors.restDay ? "is-invalid" : ""
                      }`}
                      label="Rest Day"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      // readOnly={false}
                      maxLength={100}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="salaryPeriod"
                      type="text"
                      placeholder="Salary Period"
                      className={`form-control ${
                        errors.salaryPeriod ? "is-invalid" : ""
                      }`}
                      label="Salary Period"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="basicPay"
                      type="text"
                      placeholder="Basic Pay"
                      className={`form-control ${
                        errors.basicPay ? "is-invalid" : ""
                      }`}
                      label="Basic Pay"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="grossPay"
                      type="text"
                      placeholder="Gross Pay"
                      className={`form-control ${
                        errors.grossPay ? "is-invalid" : ""
                      }`}
                      label="Gross Pay"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="salaryType"
                      type="text"
                      placeholder="Salary Type"
                      className={`form-control ${
                        errors.salaryType ? "is-invalid" : ""
                      }`}
                      label="Salary Type"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="salaryDate"
                      type="text"
                      placeholder="Salary Date"
                      className={`form-control ${
                        errors.salaryDate ? "is-invalid" : ""
                      }`}
                      label="Salary Date"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  {/* <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="fixedAllowance"
                      type="text"
                      placeholder="Fixed Allowance"
                      className={`form-control ${
                        errors.fixedAllowance ? "is-invalid" : ""
                      }`}
                      label="Fixed Allowance"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="fixedDeduction"
                      type="text"
                      placeholder="Fixed Deduction"
                      className={`form-control ${
                        errors.fixedDeduction ? "is-invalid" : ""
                      }`}
                      label="Fixed Deduction"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div> */}

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="annualLeave"
                      type="text"
                      placeholder="Annual Leave"
                      className={`form-control ${
                        errors.annualLeave ? "is-invalid" : ""
                      }`}
                      label="Annual Leave"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      maxLength={10}
                      // readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="sickLeave"
                      type="text"
                      placeholder="Sick Leave"
                      className={`form-control ${
                        errors.sickLeave ? "is-invalid" : ""
                      }`}
                      label="Sick Leave"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      maxLength={10}
                      // readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="hospitalisationLeave"
                      type="text"
                      placeholder="Hospitalisation Leave"
                      className={`form-control ${
                        errors.hospitalisationLeave ? "is-invalid" : ""
                      }`}
                      label="Hospitalisation Leave"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      maxLength={10}
                      // readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="otherLeave"
                      type="text"
                      placeholder="Other Leave"
                      className={`form-control ${
                        errors.otherLeave ? "is-invalid" : ""
                      }`}
                      label="Other Leave"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      // readOnly={true}
                      maxLength={250}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="probationLength"
                      type="text"
                      placeholder="Probation Length"
                      className={`form-control ${
                        errors.probationLength ? "is-invalid" : ""
                      }`}
                      label="Probation Length"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="probationStart"
                      type="date"
                      placeholder="Probation Start"
                      className={`form-control ${
                        errors.probationStart ? "is-invalid" : ""
                      }`}
                      label="Probation Start"
                      labelClassName="form-label"
                      defaultValue=""
                      dateFormat="YYYY-MM-DD"
                      required={true}
                      disabled={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="probationEnd"
                      type="date"
                      placeholder="Probation End"
                      className={`form-control ${
                        errors.probationEnd ? "is-invalid" : ""
                      }`}
                      label="Probation End"
                      labelClassName="form-label"
                      defaultValue=""
                      dateFormat="YYYY-MM-DD"
                      required={true}
                      disabled={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="noticePeriod"
                      type="text"
                      placeholder="Notice Period"
                      className={`form-control ${
                        errors.noticePeriod ? "is-invalid" : ""
                      }`}
                      label="Notice Period"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  {/* <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="issuedDate"
                      type="date"
                      placeholder="Issued Date"
                      className={`form-control ${
                        errors.issuedDate ? "is-invalid" : ""
                      }`}
                      label="Issued Date"
                      labelClassName="form-label"
                      defaultValue=""
                      dateFormat="YYYY-MM-DD"
                      required={true}
                      readOnly={true}
                    />
                  </div> */}

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="overtimeDate"
                      type="text"
                      placeholder="Overtime Date"
                      className={`form-control ${
                        errors.overtimeDate ? "is-invalid" : ""
                      }`}
                      label="Overtime Date"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="otherSalaryComponents"
                      type="text"
                      placeholder="Other Salary Components"
                      className={`form-control ${
                        errors.otherSalaryComponents ? "is-invalid" : ""
                      }`}
                      label="Other Salary Components"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      maxLength={200}
                      // readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="overtimeRate"
                      type="text"
                      placeholder="Overtime Rate"
                      className={`form-control ${
                        errors.overtimeRate ? "is-invalid" : ""
                      }`}
                      label="Overtime Rate"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3 ">
                    <Input
                      name="haveCpf"
                      type="commonSelect"
                      placeholder=""
                      className={`form-control ${
                        errors.haveCpf ? "is-invalid" : ""
                      }`}
                      label="Have Cpf"
                      labelClassName="form-label"
                      optionData={[
                        { id: 1, label: "Yes", value: true },
                        { id: 0, label: "No", value: false },
                      ]}
                      required={true}
                      requiredMessage=""
                      onSelect={(selectedOption) => {
                        setValue("haveCpf", selectedOption);
                      }}
                      defaultValue={watch("haveCpf")}
                      disabled={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="medicalBenefits"
                      type="text"
                      placeholder="Medical Benefits"
                      className={`form-control ${
                        errors.medicalBenefits ? "is-invalid" : ""
                      }`}
                      label="Medical Benefits"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      maxLength={500}
                      // readOnly={true}
                    />
                  </div>

                  <div className="col-md-4 col-sg-12 mb-3">
                    <Input
                      name="noticePeriodEmployment"
                      type="text"
                      placeholder="Notice Period (Employment)"
                      className={`form-control ${
                        errors.noticePeriodEmployment ? "is-invalid" : ""
                      }`}
                      label="Notice Period (Employment)"
                      labelClassName="form-label"
                      defaultValue=""
                      required={true}
                      maxLength={200}
                      // readOnly={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default KetForm;
